import { SheetFrameContext } from 'components/bottomsheet/components/AboveSheet';
import { BottomSheetDragHandle } from 'components/bottomsheet/components/BottomSheetDragHandle';
import DragBar from 'components/bottomsheet/components/DragBar';
import ScrollBar from 'components/bottomsheet/components/ScrollBar';
import { AnimatePresence, animate, domMax, LazyMotion, m, useTransform } from 'framer-motion';
import useBottomSheet from 'hooks/common/useBottomSheet';
import { useBottomSheetDrag } from 'hooks/common/useBottomSheetDrag';
import useTouchAction from 'hooks/common/useTouchAction';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SHEET_MOTION } from 'stores/bottomSheetStore';
import { BottomSheetProps, DismissReason } from 'types/bottomsheet';

export const DRAG_BAR_HEIGHT_REM = 2.5;
const DRAG_HANDLE_HEIGHT = 100;
const DRAG_THRESHOLD = 6;
const BACKDROP_OPACITY = 0.5;

export const BottomSheet: React.FC<BottomSheetProps> = ({ id, children, sheet }) => {
  const { y, dim } = sheet;
  const closing = sheet.status === 'closing';
  const { closeWithAnimation, setStatus } = useBottomSheet();

  const sheetRef = useRef<HTMLDivElement>(null); // 스크롤 컨테이너
  const [sheetHeight, setSheetHeight] = useState(0);
  const [sheetInset, setSheetInset] = useState({ left: 0, right: 0 });
  const { fullScreen, background, draggable, blur, containerStyle, zIndex, maxHeight, showScrollBar } = sheet.options;
  const isRTL = BResource.isRTL();
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
  const backdropOpacity = useTransform(dim, (d) => BACKDROP_OPACITY * d);

  const dismiss = (reason: DismissReason, velocity = 0) => closeWithAnimation(id, { skipConfirm: true, data: null, velocity, reason });
  const { controls, onDragStart, onDrag, onDragEnd, release, setOffset } = useBottomSheetDrag({ y, dim, onClose: (velocity) => dismiss('drag', velocity) });

  const onTouchAction = (overscrollDistance: number, touchEnd: boolean, velocity: number) => {
    if (closing) return;
    if (touchEnd) {
      if (overscrollDistance > 0) release(overscrollDistance, velocity);
      return;
    }
    setOffset(overscrollDistance);
  };

  useEffect(() => {
    animate(dim, 1, { duration: SHEET_MOTION.dimDuration, ease: SHEET_MOTION.dimEase });
  }, [dim]);

  // 스크롤 최상단에서 드래그 동작으로 넘어가게 하기 위한 훅
  useTouchAction({ containerRef: sheetRef, onTouchAction });

  // sheet 높이 변화 감지
  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      const totalHeight = rect.height;
      setSheetHeight(totalHeight);
      setSheetInset({ left: rect.left, right: window.innerWidth - rect.right });
    });

    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const portalRoot = useMemo(() => sheet.mountTo ?? (typeof document !== 'undefined' ? document.getElementById('hMainContainer') : null), [sheet.mountTo]);
  if (!portalRoot) return null;

  return createPortal(
    <AnimatePresence>
      <LazyMotion features={domMax} strict>
        <div data-component="BottomSheet" className="pointer-events-none fixed inset-0 z-[102]" style={{ direction: isRTL ? 'rtl' : 'ltr', zIndex }}>
          {/*backdrop */}
          <m.div
            id="bottom-sheet-backdrop"
            onClick={() => dismiss('backdrop')}
            className="pointer-events-auto fixed inset-0"
            style={{ opacity: backdropOpacity, backgroundColor: '#000' }}
          />
          {/* sheet */}
          <m.div
            data-bottom-sheet-id={id}
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            className={`scroll-container no-scrollbar pointer-events-auto fixed bottom-0 left-0 right-0 z-[1] max-h-[100%] min-h-[200px] rounded-t-[24px] p-4 text-dialog-txt-default`}
            initial={{ y: viewportHeight }}
            animate={{ y: 0 }}
            onAnimationComplete={() => {
              setStatus(id, 'opened');
            }}
            transition={{ duration: SHEET_MOTION.openDuration, ease: SHEET_MOTION.openEase, delay: SHEET_MOTION.openDelay }}
            drag={draggable && !closing ? 'y' : false}
            dragControls={controls}
            dragDirectionLock
            dragElastic={0.5}
            dragMomentum={false}
            dragListener={false}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDrag={onDrag}
            style={{
              y,
              touchAction: 'pan-y',
              height: fullScreen ? '100%' : undefined,
              maxHeight: fullScreen ? undefined : `var(--sheet-max-height, ${maxHeight})`, // 가로 모드는 CSS가 변수로 덮어씀
              borderRadius: fullScreen ? 0 : undefined,
              background: background || 'var(--bcs-semantic-dialog-bg-default)',
              backdropFilter: blur ? 'blur(32px)' : undefined,
              paddingBottom: 'calc(var(--navigation-bar-height) + 16px)',
              paddingTop: fullScreen ? `calc(var(--status-bar-height) + 16px)` : `${draggable ? `${DRAG_BAR_HEIGHT_REM}rem` : 0}`,
              overflowY: 'auto',
              ...containerStyle,
              overscrollBehavior: 'none',
            }}
          >
            <div className="content-container relative h-full w-full">
              {draggable ? (
                <BottomSheetDragHandle
                  controls={controls}
                  y={y}
                  height={DRAG_HANDLE_HEIGHT}
                  dragThreshold={DRAG_THRESHOLD}
                  offsetY={typeof window !== 'undefined' ? window.innerHeight - sheetHeight : 0}
                />
              ) : null}
              <SheetFrameContext.Provider
                value={sheetHeight > 0 ? { y, top: window.innerHeight - sheetHeight, left: sheetInset.left, right: sheetInset.right, zIndex, mountTo: portalRoot } : null}
              >
                {children}
              </SheetFrameContext.Provider>
              {showScrollBar && sheetHeight > 0 ? (
                <ScrollBar zIndex={zIndex} y={y} mountTo={portalRoot} offsetY={window.innerHeight - sheetHeight} delay={0} />
              ) : null}
              {draggable && sheetHeight > 0 ? (
                <DragBar y={y} offsetY={window.innerHeight - sheetHeight} zIndex={zIndex} mountTo={portalRoot} delay={0} />
              ) : null}
            </div>
          </m.div>
        </div>
      </LazyMotion>
    </AnimatePresence>,
    portalRoot
  );
};
